import { useEffect, useState } from "react";
import { http } from "@/api/client";
import { Link } from "react-router-dom";
import { Lock, Play, BookOpen, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LEVELS = [
  { key: "quiz", label: "Niveau 1", time: 30, color: "#1368ce" },
  { key: "difficile", label: "Niveau 2", time: 25, color: "#d89e00" },
  { key: "legend", label: "Légende", time: 15, color: "#e21b3c" },
];

export default function Courses() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [intros, setIntros] = useState({});
  const [open, setOpen] = useState(null);
  const [levels, setLevels] = useState({});

  useEffect(() => {
    http.get("/courses").then((r) => setCourses(r.data));
    http.get("/intros").then((r) => {
      const map = {}; r.data.forEach(i => map[i.competency] = i.text);
      setIntros(map);
    }).catch(()=>{});
  }, []);

  async function toggle(cid) {
    if (open === cid) { setOpen(null); return; }
    setOpen(cid);
    if (!levels[cid] && user.role === "eleve") {
      try {
        const r = await http.get(`/courses/${cid}/levels`);
        setLevels((prev) => ({ ...prev, [cid]: r.data }));
      } catch (err) {
        console.error("Failed to load course levels:", err);
      }
    }
  }

  const byComp = courses.reduce((acc, c) => { (acc[c.competency] ||= []).push(c); return acc; }, {});
  const COMP_TITLES = {
    1: "Maîtriser le véhicule",
    2: "Code de la route (conditions normales)",
    3: "Circuler dans des conditions difficiles",
    4: "Conduire de manière autonome",
  };

  return (
    <div className="space-y-8" data-testid="courses-page">
      <div>
        <h1 className="font-headings font-black text-3xl md:text-4xl text-[#1a2332]">Catalogue des cours</h1>
        <p className="mt-2 text-[#5b6577]">4 compétences · {courses.length} cours structurés selon la méthode EDGAR.</p>
        <div className="mt-3 inline-flex items-center gap-2 text-xs bg-white border border-black/5 rounded-full px-3 py-1.5 shadow-sm">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#00a86b]" />
          Règle : max 1 faute pour débloquer le niveau suivant · tentatives illimitées
        </div>
      </div>

      {[1,2,3,4].map((comp) => (
        <div key={comp} data-testid={`competency-${comp}`}>
          <div className="mb-4">
            <div className="font-headings font-black text-2xl text-[#00a86b]">Compétence {comp} — <span className="text-[#1a2332]">{COMP_TITLES[comp]}</span></div>
            {intros[comp] && <div className="mt-2 text-sm text-[#5b6577] max-w-3xl italic">{intros[comp]}</div>}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(byComp[comp] || []).map((c) => (
              <div key={c.id} data-testid={`course-${c.id}`}
                className={`rounded-2xl p-5 border transition-colors ${c.unlocked ? "bg-white border-black/5 shadow-sm hover:shadow-md hover:border-[#00a86b]/30" : "bg-[#f3f5fa] border-black/5"}`}>
                <Link to={`/app/courses/${c.id}`} className="block" data-testid={`course-${c.id}-open`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-xs text-[#9aa5bc] font-mono">{`C${c.competency}-${String(c.order).padStart(2, "0")}`}</div>
                      <div className={`font-headings font-bold mt-1 ${c.unlocked ? "text-[#1a2332]" : "text-[#1a2332]/50"}`}>{c.title}</div>
                      <div className="text-xs text-[#5b6577] mt-2">{c.key_points}</div>
                    </div>
                    {c.unlocked ? <BookOpen className="w-5 h-5 text-[#00a86b]" /> : <Lock className="w-5 h-5 text-[#9aa5bc]" />}
                  </div>
                </Link>
                {!c.unlocked && user.role === "eleve" && (
                  <div className="mt-3 text-xs text-[#5b6577] italic" data-testid={`course-${c.id}-hint`}>
                    Verrouillé — demandez le déblocage à votre mentor.
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {c.unlocked && (
                    <Link to={`/app/courses/${c.id}`} data-testid={`course-${c.id}-read`}
                      className="inline-flex items-center gap-1.5 bg-[#00a86b] hover:bg-[#004799] text-white font-headings font-bold rounded-lg px-3 py-1.5 text-xs">
                      <BookOpen className="w-3.5 h-3.5" /> Lire le cours
                    </Link>
                  )}
                  {c.unlocked && c.has_quiz && (
                    <button onClick={(e) => { e.preventDefault(); toggle(c.id); }} data-testid={`course-${c.id}-start`}
                      className="inline-flex items-center gap-1.5 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-lg px-3 py-1.5 text-xs">
                      <Play className="w-3.5 h-3.5" /> {open === c.id ? "Masquer" : "Quiz"}
                    </button>
                  )}
                </div>
                {open === c.id && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {LEVELS.map((l) => {
                      const st = levels[c.id]?.[l.key];
                      const isLocked = user.role === "eleve" && st && !st.unlocked;
                      const isPassed = st?.passed;
                      if (isLocked) {
                        return (
                          <div key={l.key} data-testid={`quiz-level-${c.id}-${l.key}-locked`}
                            className="relative rounded-lg px-3 py-2.5 text-sm font-headings font-bold bg-[#f3f5fa] border border-black/5 text-[#9aa5bc] flex items-center justify-center gap-1.5">
                            <Lock className="w-3.5 h-3.5" /> {l.label}
                          </div>
                        );
                      }
                      return (
                        <Link key={l.key} to={`/app/courses/${c.id}/quiz/${l.key}`}
                          data-testid={`quiz-level-${c.id}-${l.key}`}
                          className="relative rounded-lg px-3 py-2.5 text-sm font-headings font-bold text-white hover:opacity-90 flex items-center justify-center shadow-sm"
                          style={{ backgroundColor: l.color }}>
                          {isPassed && <CheckCircle2 className="w-3.5 h-3.5 absolute top-1.5 right-1.5 text-white" />}
                          {l.label} · {l.time}s
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
